<template>
    <div class="absence-report" dir="rtl">
        <Breadcrumb :items="breadcrumbs" />
        <div class="container-fluid my-3">
        <div class="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
            <h4 class="mb-0">تقرير الحضور والغياب <span v-if="grade" class="text-muted">— {{ grade.name }}</span></h4>
            <router-link :to="{ name: 'gradeAttendance', params: { gradeSlug } }" class="btn btn-outline-secondary">
                <i class="pi pi-arrow-right me-1" aria-hidden="true"></i> العودة للحضور
            </router-link>
        </div>

        <div class="card mb-3">
            <div class="card-body p-3">
                <div class="row g-3 align-items-end">
                    <div class="col-lg-3 col-md-6 col-12">
                        <label for="report-grade" class="form-label">المرحلة الدراسية</label>
                        <Dropdown inputId="report-grade" :modelValue="gradeSlug" :options="gradeOptions" optionLabel="name"
                            optionValue="slug" class="w-100" @change="changeGrade($event.value)" />
                    </div>

                    <div class="col-lg-3 col-md-6 col-12">
                        <label for="report-mode" class="form-label">نوع التقرير</label>
                        <Dropdown inputId="report-mode" v-model="mode" :options="modes" optionLabel="name"
                            optionValue="id" class="w-100" @change="fetchReport" />
                    </div>
                    <div class="col-lg-3 col-md-6 col-12">
                        <template v-if="mode === 'month'">
                            <label for="report-month" class="form-label">الشهر</label>
                            <input id="report-month" type="month" class="form-control" v-model="month"
                                :max="today.slice(0, 7)" @change="fetchReport" />
                        </template>
                        <template v-else-if="mode === 'day'">
                            <label for="report-date" class="form-label">التاريخ</label>
                            <input id="report-date" type="date" class="form-control" v-model="date"
                                :max="today" @change="fetchReport" />
                        </template>
                        <template v-else>
                            <label for="report-from" class="form-label">من تاريخ</label>
                            <input id="report-from" type="date" class="form-control" v-model="startDate" @change="fetchReport" />
                        </template>
                    </div>
                    <div class="col-lg-3 col-md-6 col-12" v-if="mode === 'range'">
                        <label for="report-to" class="form-label">إلى تاريخ</label>
                        <input id="report-to" type="date" class="form-control" v-model="endDate" @change="fetchReport" />
                    </div>
                    <div class="col-lg-3 col-md-6 col-12">
                        <label for="report-search" class="form-label">اسم الطالب أو رقم الحجز</label>
                        <input id="report-search" type="search" class="form-control" v-model="word"
                            placeholder="ابحث بالاسم أو رقم الحجز" @input="debounceFetch" />
                    </div>
                    <div class="col-lg-3 col-md-6 col-12">
                        <label for="report-gender" class="form-label">الجنس</label>
                        <Dropdown inputId="report-gender" v-model="gender" :options="genders" optionLabel="title"
                            optionValue="id" showClear placeholder="الكل" class="w-100" @change="fetchReport" />
                    </div>
                    <div class="col-lg-3 col-md-6 col-12">
                        <label for="report-group" class="form-label">المجموعة</label>
                        <Dropdown inputId="report-group" v-model="groupId" :options="groups" optionLabel="name"
                            optionValue="id" showClear placeholder="كل المجموعات" class="w-100" @change="fetchReport" />
                    </div>
                    <template v-if="mode !== 'day'">
                        <div class="col-lg-3 col-md-6 col-12">
                            <label for="attendance-count" class="form-label">عدد أيام الحضور</label>
                            <div class="d-flex gap-2">
                                <Dropdown inputId="attendance-operator" aria-label="شرط الحضور" v-model="attendanceOperator"
                                    :options="comparisons" optionLabel="name" optionValue="id" class="w-100" @change="fetchReport" />
                                <input id="attendance-count" type="number" min="0" step="1" class="form-control count-input"
                                    v-model="attendanceCount" placeholder="الكل" @input="debounceFetch" />
                            </div>
                        </div>
                        <div class="col-lg-3 col-md-6 col-12">
                            <label for="absence-count" class="form-label">عدد أيام الغياب</label>
                            <div class="d-flex gap-2">
                                <Dropdown inputId="absence-operator" aria-label="شرط الغياب" v-model="absenceOperator"
                                    :options="comparisons" optionLabel="name" optionValue="id" class="w-100" @change="fetchReport" />
                                <input id="absence-count" type="number" min="0" step="1" class="form-control count-input"
                                    v-model="absenceCount" placeholder="الكل" @input="debounceFetch" />
                            </div>
                        </div>
                    </template>
                    <div class="col-12 d-flex gap-2 flex-wrap">
                        <button type="button" class="btn PrimaryButton" @click="fetchReport">تطبيق الفلاتر</button>
                        <button type="button" class="btn btn-outline-secondary" @click="resetFilters">إعادة تعيين</button>
                    </div>
                </div>
                <p v-if="mode === 'range'" class="text-muted small mt-3 mb-0">تُحسب الأيام المجدولة لكل مجموعة داخل الفترة، حتى اليوم فقط. الطلاب بدون مجموعة أو جدول محدد لا تُحتسب لهم أيام حضور أو غياب مجدولة.</p>
                <p v-else class="text-muted small mt-3 mb-0">يُحسب الغياب فقط في أيام المجموعة التي سُجّل فيها حضور فعلي، حتى اليوم. الأيام دون تسجيل والأيام غير المحددة للمجموعة لا تُحتسب.</p>
                <p class="text-muted small mt-1 mb-0">السجلات القديمة تُستخدم فقط عندما يمكن ربطها بالمجموعة؛ يُطبّق أول جدول محدد على الجلسات التي لم يكن لها جدول محفوظ.</p>
            </div>
        </div>

        <div class="d-flex gap-2 flex-wrap mb-3">
            <button type="button" class="btn btn-success" :disabled="!canExport" @click="exportToExcel">
                <i class="pi pi-file-excel me-1" aria-hidden="true"></i> تصدير Excel
            </button>
            <button type="button" class="btn btn-danger" :disabled="!canExport" v-print="'#absenceReportTable'">
                <i class="pi pi-print me-1" aria-hidden="true"></i> طباعة / PDF
            </button>
        </div>

        <Loading v-if="loading" />
        <div v-else-if="error" class="alert alert-danger" role="alert">
            {{ error }}
            <button type="button" class="btn btn-sm btn-outline-danger ms-2" @click="initialize">إعادة المحاولة</button>
        </div>
        <div v-else-if="report" id="absenceReportTable" dir="rtl">
            <div class="card mb-3">
                <div class="card-body p-3">
                    <h5>{{ periodTitle }} <bdi>{{ report.period }}</bdi></h5>
                    <p class="mb-1">{{ report.academic_year.name }}</p>
                    <p class="mb-1" v-if="report.through_date">الاحتساب حتى: <bdi>{{ report.through_date }}</bdi></p>
                    <div class="d-flex gap-3 flex-wrap">
                        <span>عدد الطلاب: <strong>{{ report.returned_students ?? report.students.length }}</strong></span>
                        <span v-if="report.mode !== 'day'">إجمالي أيام الحضور: <strong>{{ report.total_attendance_days }}</strong></span>
                        <span>طلاب لديهم غياب: <strong>{{ report.students_with_absences }}</strong></span>
                        <span v-if="report.mode !== 'day'">إجمالي أيام الغياب: <strong>{{ report.total_absence_days }}</strong></span>
                    </div>
                </div>
            </div>
            <div class="table-responsive">
                <table class="table table-bordered align-middle">
                    <thead>
                        <tr>
                            <th scope="col">اسم الطالب</th>
                            <th scope="col">رقم الحجز</th>
                            <th scope="col">رقم ولي الأمر</th>
                            <th scope="col">المجموعة</th>
                            <th v-if="report.mode === 'day'" scope="col">تاريخ الغياب</th>
                            <template v-else>
                                <th scope="col" class="text-center">عدد أيام الحضور</th>
                                <th scope="col" class="text-center">عدد أيام الغياب</th>
                            </template>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="student in report.students" :key="student.user_id">
                            <td>{{ student.name }}</td>
                            <td><bdi>{{ student.reservation_number }}</bdi></td>
                            <td><bdi>{{ student.parent_phone || '—' }}</bdi></td>
                            <td>{{ student.group_name || '—' }}</td>
                            <td v-if="report.mode === 'day'"><bdi>{{ student.date ?? report.period }}</bdi></td>
                            <template v-else>
                                <td class="text-center fw-bold text-success">{{ student.attendance_days }}</td>
                                <td class="text-center fw-bold text-danger">{{ student.absence_days }}</td>
                            </template>
                        </tr>
                        <tr v-if="!report.students.length">
                            <td :colspan="report.mode === 'day' ? 5 : 6" class="text-center text-muted py-5" role="status">
                                {{ report.mode === 'day' ? 'لا يوجد طلاب غائبون في التاريخ المحدد وفق الفلاتر الحالية.' : report.mode === 'range' ? 'لا يوجد طلاب مطابقون للفلاتر خلال الفترة المحددة.' : 'لا توجد حالات غياب للشهر المحدد وفق الفلاتر الحالية.' }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import Dropdown from 'primevue/dropdown';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';
import * as XLSX from 'xlsx/xlsx.mjs';

function cairoToday() {
    const parts = new Intl.DateTimeFormat('en', {
        timeZone: 'Africa/Cairo', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date());
    const value = type => parts.find(part => part.type === type).value;
    return [value('year'), value('month'), value('day')].join('-');
}

export default {
    components: { Dropdown, Breadcrumb, Loading },
    data() {
        const today = cairoToday();
        const query = this.$route.query;
        return {
            today,
            mode: ['month', 'day', 'range'].includes(query.mode) ? query.mode : 'month',
            month: /^\d{4}-(0[1-9]|1[0-2])$/.test(query.month ?? '') ? query.month : today.slice(0, 7),
            date: /^\d{4}-\d{2}-\d{2}$/.test(query.date ?? '') ? query.date : today,
            startDate: /^\d{4}-\d{2}-\d{2}$/.test(query.start_date ?? '') ? query.start_date : today.slice(0, 7) + '-01',
            endDate: /^\d{4}-\d{2}-\d{2}$/.test(query.end_date ?? '') ? query.end_date : today,
            attendanceCount: /^\d+$/.test(query.attendance_count ?? '') ? query.attendance_count : '',
            absenceCount: /^\d+$/.test(query.absence_count ?? '') ? query.absence_count : '',
            attendanceOperator: ['lt', 'eq', 'gt', 'lte', 'gte'].includes(query.attendance_operator) ? query.attendance_operator : 'lt',
            absenceOperator: ['lt', 'eq', 'gt', 'lte', 'gte'].includes(query.absence_operator) ? query.absence_operator : 'gte',
            comparisons: [
                { id: 'lt', name: 'أقل من' }, { id: 'eq', name: 'يساوي' }, { id: 'gt', name: 'أكبر من' },
                { id: 'lte', name: 'أقل من أو يساوي' }, { id: 'gte', name: 'أكبر من أو يساوي' },
            ],
            word: typeof query.word === 'string' ? query.word : '',
            gender: ['1', '2'].includes(String(query.gender)) ? Number(query.gender) : null,
            modes: [{ id: 'month', name: 'ملخص الغياب الشهري' }, { id: 'day', name: 'غياب يوم محدد' }, { id: 'range', name: 'تقرير الغياب خلال فترة' }],
            genders: [{ id: 1, title: 'ولد' }, { id: 2, title: 'بنت' }],
            groups: [],
            groupId: /^\d+$/.test(query.group_id ?? '') ? Number(query.group_id) : null,
            report: null,
            loading: true,
            error: '',
            searchTimeout: null,
            requestId: 0,
        };
    },
    computed: {
        gradeStore() { return useGradeStore(); },
        gradeSlug() { return this.$route.params.gradeSlug; },
        grade() {
            return this.gradeStore.allGrades.find(g => g.slug === this.gradeSlug)
                || this.gradeStore.activeGrades.find(g => g.slug === this.gradeSlug);
        },
        gradeOptions() { return this.gradeStore.allGrades.length ? this.gradeStore.allGrades : this.gradeStore.activeGrades; },
        breadcrumbs() {
            return [
                { name: 'الرئيسية', path: '/' },
                { name: this.grade?.name ?? this.gradeSlug, path: '/grades/' + this.gradeSlug + '/students' },
                { name: 'الحضور', path: '/grades/' + this.gradeSlug + '/attendance' },
                { name: 'تقرير الغياب', path: this.$route.fullPath },
            ];
        },
        periodTitle() {
            if (!this.report) return '';
            return this.report.mode === 'range' ? 'تقرير الحضور والغياب خلال الفترة:'
                : this.report.mode === 'day' ? 'تقرير الغياب ليوم:' : 'ملخص الغياب لشهر:';
        },
        canExport() {
            return !this.loading && !this.error && !!this.report?.students.length;
        },
    },
    watch: {
        gradeSlug() {
            this.groupId = null;
            this.initialize();
        },
    },
    methods: {
        changeGrade(slug) {
            if (slug === this.gradeSlug) return;
            this.requestId++;
            this.loading = true;
            this.report = null;
            this.$router.push({ name: 'gradeAttendanceAbsenceReport', params: { gradeSlug: slug } });
        },
        async initialize() {
            const initializationId = ++this.requestId;
            this.loading = true;
            this.error = '';
            this.report = null;
            try {
                if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
                if (initializationId !== this.requestId) return;
                if (!this.grade) throw new Error('grade');
                const { data } = await axios.get('/academic-years/' + this.grade.id + '/groups');
                if (initializationId !== this.requestId) return;
                this.groups = data.data ?? [];
                await this.fetchReport();
            } catch {
                if (initializationId !== this.requestId) return;
                this.error = 'تعذّر تحميل بيانات الفصل. يرجى إعادة المحاولة.';
                this.loading = false;
            }
        },
        debounceFetch() {
            clearTimeout(this.searchTimeout);
            // Hide the previous result and disable export as soon as filters change.
            this.requestId++;
            this.loading = true;
            this.searchTimeout = setTimeout(() => this.fetchReport(), 350);
        },
        resetFilters() {
            this.mode = 'month';
            this.month = this.today.slice(0, 7);
            this.date = this.today;
            this.startDate = this.month + '-01';
            this.endDate = this.today;
            this.word = '';
            this.gender = null;
            this.groupId = null;
            this.attendanceCount = '';
            this.absenceCount = '';
            this.attendanceOperator = 'lt';
            this.absenceOperator = 'gte';
            return this.fetchReport();
        },
        async fetchReport() {
            clearTimeout(this.searchTimeout);
            const requestId = ++this.requestId;
            this.loading = true;
            this.error = '';
            this.report = null;
            const periodValid = this.mode === 'range' ? this.startDate && this.endDate
                : this.mode === 'day' ? this.date : this.month;
            if (!this.grade || !periodValid) {
                this.error = 'يرجى تحديد الفصل والفترة المطلوبة.';
                this.loading = false;
                return;
            }
            if (this.mode === 'range' && this.startDate > this.endDate) {
                this.error = 'يجب أن يكون تاريخ النهاية مساويًا لتاريخ البداية أو بعده.';
                this.loading = false;
                return;
            }
            const params = {
                academic_year_id: this.grade.id,
                mode: this.mode,
                ...(this.mode === 'range' ? { start_date: this.startDate, end_date: this.endDate }
                    : this.mode === 'day' ? { date: this.date } : { month: this.month }),
            };
            if (this.mode !== 'day') {
                for (const [kind, count, operator] of [
                    ['attendance', this.attendanceCount, this.attendanceOperator],
                    ['absence', this.absenceCount, this.absenceOperator],
                ]) {
                    if (count === '' || count == null) continue;
                    if (!Number.isInteger(Number(count)) || Number(count) < 0) {
                        this.error = 'يجب أن يكون عدد الأيام عددًا صحيحًا لا يقل عن صفر.';
                        this.loading = false;
                        return;
                    }
                    params[kind + '_count'] = Number(count);
                    params[kind + '_operator'] = operator;
                }
            }
            if (this.word.trim()) params.word = this.word.trim();
            if (this.gender) params.gender = this.gender;
            if (this.groupId) params.group_id = this.groupId;
            try {
                const { data } = await axios.get('/attendance-absence-report', { params });
                if (requestId !== this.requestId) return;
                this.report = data.data;
            } catch (error) {
                if (requestId !== this.requestId) return;
                this.error = Object.values(error.response?.data?.errors ?? {}).flat()[0]
                    || 'تعذّر تحميل تقرير الغياب. يرجى التحقق من الفلاتر وإعادة المحاولة.';
            } finally {
                if (requestId === this.requestId) this.loading = false;
            }
        },
        exportToExcel() {
            if (!this.canExport) return;
            const range = this.report.mode === 'range';
            const day = this.report.mode === 'day';
            const headers = ['اسم الطالب', 'رقم الحجز', 'رقم ولي الأمر',
                ...(range ? ['المجموعة', 'الفترة من', 'الفترة إلى'] : [day ? 'تاريخ الغياب' : 'الشهر', 'المجموعة']),
                ...(!day ? ['عدد أيام الحضور', 'عدد أيام الغياب'] : []),
            ];
            // Export exactly the server-filtered rows. Keep phone/booking cells as strings.
            const rows = this.report.students.map(student => [
                student.name, String(student.reservation_number), String(student.parent_phone ?? ''),
                ...(range ? [student.group_name ?? '', this.report.start_date, this.report.end_date]
                    : [day ? student.date ?? this.report.period : this.report.period, student.group_name ?? '']),
                ...(!day ? [student.attendance_days, student.absence_days] : []),
            ]);
            const sheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
            sheet['!cols'] = headers.map((_, index) => ({ wch: index === 0 ? 30 : 20 }));
            const book = XLSX.utils.book_new();
            book.Workbook = { Views: [{ RTL: true }] };
            XLSX.utils.book_append_sheet(book, sheet, 'تقرير الغياب');
            const period = range ? this.report.start_date + '_' + this.report.end_date : this.report.period;
            XLSX.writeFile(book, 'تقرير-الغياب-' + this.report.academic_year.name + '-' + period + '.xlsx');
        },
    },
    mounted() { this.initialize(); },
    beforeUnmount() {
        clearTimeout(this.searchTimeout);
        this.requestId++;
    },
};
</script>

<style scoped>
.absence-report .table {
    min-width: 760px;
}
#absenceReportTable .table tbody td {
    color: var(--table-cell-color);
    background-color: var(--table-cell-bg);
}
#absenceReportTable .table thead th {
    color: var(--table-cell-color) !important;
    background: var(--table-stripe-bg) !important;
}
.absence-report input[type="date"],
.absence-report input[type="month"] {
    direction: ltr;
    text-align: right;
}
.count-input { max-width: 110px; }
@media print {
    /* Other legacy pages hide body contents and columns globally when printing. */
    #absenceReportTable,
    #absenceReportTable * { visibility: visible !important; }
    #absenceReportTable th,
    #absenceReportTable td { display: table-cell !important; }
    #absenceReportTable .table { min-width: 0; width: 100%; }
    .table-responsive { overflow: visible; }
    #absenceReportTable {
        direction: rtl;
    }
    #absenceReportTable .card-body {
        color: var(--table-cell-color);
        background: var(--table-cell-bg);
    }
}
</style>
